/**
 * Shared constants for the onboarding demo flow.
 *
 * The onboarding experience plays out across two scenes:
 *
 *   Scene 1 — /replay/{ONBOARDING_DEMO_CURATED_ID}
 *     A curated replay of the original chat session that built
 *     "Weekly Bullish Plays" (a real, live alert). The buddy idles in
 *     the corner during replay, then morphs into a spotlight tour when
 *     the replay finishes.
 *
 *   Scene 2 — /alerts/{ONBOARDING_DEMO_ALERT_ID}?onboarding=1
 *     The user clicks the buddy's CTA at the end of scene 1, which
 *     navigates to the real alert that was created in the recording.
 *     Scene 2 walks them through the alert canvas (notebook / schedule
 *     / history / chat) and ends on a "see plans" CTA.
 *
 * Why both IDs are public:
 *   - The curated ID resolves to a real session in
 *     `chat_history.sessions_xynth_2_0` via the curated_sessions doc
 *     `onboarding_three_step_demo`.
 *   - The alert ID is a real, public alert (is_public=true) so visitors
 *     get the proper read-only public canvas. Owner is the founder
 *     account; the public view doesn't expose owner email.
 *
 * Resource model:
 *   - The replay backend is in-memory + ephemeral per-replay session
 *     (no DB writes during replay).
 *   - The alert is *one* live alert that runs on its own schedule. We
 *     do not run it per-visitor; visitors share whatever last run
 *     happens to be cached. Run history accrues naturally over time.
 *   - Per-visitor visitor-chat sessions are created lazily by the
 *     existing alert page flow ONLY when the user actually submits a
 *     message (gated by AuthModal/PlansModal upstream).
 */

/** curated_sessions.curated_id pointing at the source chat session. */
export const ONBOARDING_DEMO_CURATED_ID = "market_edge_scan_setups";

/** The real alert created at the end of the recorded session. */
export const ONBOARDING_DEMO_ALERT_ID = "alert-7a209a4f9f5d";

/** URL query param that flips the alert page into onboarding mode
 *  (kicks off scene 2). Stripped after the tour completes so a refresh
 *  doesn't re-fire the spotlight. */
export const ONBOARDING_QUERY_PARAM = "onboarding";
export const ONBOARDING_QUERY_VALUE = "1";
export const ONBOARDING_START_QUERY_PARAM = "onboarding_start";
export const ONBOARDING_START_QUERY_VALUE = "1";
export const HOME_SHOWCASE_TOUR_QUERY_PARAM = "onboarding_home";
export const HOME_SHOWCASE_TOUR_QUERY_VALUE = "1";
export const HOME_SHOWCASE_TOUR_TAB_EVENT = "xynth:home-showcase-tour-tab";
export const AUTOMATIONS_TOUR_QUERY_PARAM = "onboarding_automations";
export const AUTOMATIONS_TOUR_QUERY_VALUE = "1";
export const AUTOMATIONS_ALERT_TOUR_QUERY_PARAM = "onboarding_alert_public";
export const AUTOMATIONS_ALERT_TOUR_QUERY_VALUE = "1";

/** Trade-ideas / blog stage tours. */
export const BLOG_TOUR_QUERY_PARAM = "onboarding_blog";
export const BLOG_TOUR_QUERY_VALUE = "1";
export const BLOG_ARTICLE_TOUR_QUERY_PARAM = "onboarding_blog_article";
export const BLOG_ARTICLE_TOUR_QUERY_VALUE = "1";

/** Community stage tours. */
export const COMMUNITY_TOUR_QUERY_PARAM = "onboarding_community";
export const COMMUNITY_TOUR_QUERY_VALUE = "1";
export const COMMUNITY_CHAT_TOUR_QUERY_PARAM = "onboarding_chat_share";
export const COMMUNITY_CHAT_TOUR_QUERY_VALUE = "1";

/** Stages of the home-showcase tour beyond the original automations
 *  intro. The original `?onboarding_home=1` is treated as the
 *  "automations" stage for backwards compatibility. */
export type HomeShowcaseStage =
  | "automations"
  | "trade_ideas"
  | "community"
  | "finale";

export const HOME_SHOWCASE_STAGE_VALUES: HomeShowcaseStage[] = [
  "automations",
  "trade_ideas",
  "community",
  "finale",
];

/** sessionStorage key used to hand off between cross-page stages of
 *  the onboarding chain. Each "click the home logo" beat writes the
 *  next stage here right before the natural navigation fires; the
 *  home page reads + clears it on mount and replaces the URL with
 *  `?onboarding_home=<stage>` so the next stage tour activates and
 *  the user can refresh without losing position. */
export const HOME_TOUR_RESUME_STORAGE_KEY = "xynth_onboarding_resume";

/** Default replay speed for the onboarding curated demo. The raw 1x
 *  replay is ~5min with recovery loops; 1.5x lands around 3min which
 *  is the right pacing for a guided tour. The user can still nudge it
 *  via the speed control. */
export const ONBOARDING_REPLAY_SPEED = 1;

/** Default replay speed for the "clean" replay route
 *  (`/replay/[id]/clean`). The clean tour has no buddy beats slowing
 *  the viewer down (no read-then-click bubbles), so the recording can
 *  comfortably play at 2× and still be followable. The viewer can
 *  still nudge it via the speed control. Routed in via the homepage
 *  Cmd+Enter shortcut — see chat.tsx `onCmdEnter` wiring. */
export const CLEAN_REPLAY_SPEED = 2;

/** Window event the onboarding alert tour fires to ask the alert a
 *  pre-filled question on behalf of the user. The visitor chat in
 *  chat.tsx listens for this and runs it through the existing visitor
 *  submit gate (auth/plans modals for unauth/free tiers). Decoupled
 *  bus so the tour component doesn't need a ref into <Chat>. */
export const ONBOARDING_ASK_ALERT_EVENT = "xynth:onboarding-ask-alert";

export interface OnboardingAskAlertDetail {
  question: string;
}

/** The pre-filled question the buddy submits during scene 2 step 4.
 *  Picked to have a known-good answer derived directly from the
 *  notebook (cell purposes + alert.send template). */
export const ONBOARDING_ASK_ALERT_QUESTION =
  "What does this alert scan for, and how should I read the trade tickets it emails me?";

/** Builds the full alert URL used by scene 1's final CTA + the
 *  recorded wrap-up message link. Always includes the ?onboarding=1
 *  flag so scene 2 fires on landing. */
export function buildOnboardingAlertUrl(): string {
  return `/alerts/${ONBOARDING_DEMO_ALERT_ID}?${ONBOARDING_QUERY_PARAM}=${ONBOARDING_QUERY_VALUE}`;
}

export function buildOnboardingStartUrl(): string {
  return `/?${ONBOARDING_START_QUERY_PARAM}=${ONBOARDING_START_QUERY_VALUE}`;
}

export function buildHomeShowcaseTourUrl(
  stage: HomeShowcaseStage = "automations",
): string {
  // The original automations stage keeps `?onboarding_home=1` so old
  // links and tests continue to work; later stages use the stage name
  // as the value.
  const value = stage === "automations" ? HOME_SHOWCASE_TOUR_QUERY_VALUE : stage;
  return `/?${HOME_SHOWCASE_TOUR_QUERY_PARAM}=${value}`;
}

export function buildAutomationsTourUrl(): string {
  return `/automations?${AUTOMATIONS_TOUR_QUERY_PARAM}=${AUTOMATIONS_TOUR_QUERY_VALUE}`;
}

export function buildAutomationsAlertTourUrl(alertId: string): string {
  return `/alerts/${alertId}?${AUTOMATIONS_ALERT_TOUR_QUERY_PARAM}=${AUTOMATIONS_ALERT_TOUR_QUERY_VALUE}`;
}

export function buildBlogTourUrl(): string {
  return `/blog?${BLOG_TOUR_QUERY_PARAM}=${BLOG_TOUR_QUERY_VALUE}`;
}

export function buildBlogArticleTourUrl(slug: string): string {
  return `/blog/${slug}?${BLOG_ARTICLE_TOUR_QUERY_PARAM}=${BLOG_ARTICLE_TOUR_QUERY_VALUE}`;
}

export function buildCommunityTourUrl(): string {
  return `/community?${COMMUNITY_TOUR_QUERY_PARAM}=${COMMUNITY_TOUR_QUERY_VALUE}`;
}

export function buildCommunityChatTourUrl(sessionId: string): string {
  return `/chat/${sessionId}?${COMMUNITY_CHAT_TOUR_QUERY_PARAM}=${COMMUNITY_CHAT_TOUR_QUERY_VALUE}`;
}

/** Returns true if the current URL search params indicate the user is
 *  inside the onboarding flow (just clicked through from scene 1). */
export function isOnboardingAlertVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return searchParams.get(ONBOARDING_QUERY_PARAM) === ONBOARDING_QUERY_VALUE;
}

export function isOnboardingStartVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(ONBOARDING_START_QUERY_PARAM) === ONBOARDING_START_QUERY_VALUE ||
    searchParams.get(ONBOARDING_QUERY_PARAM) === ONBOARDING_QUERY_VALUE
  );
}

export function isHomeShowcaseTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(HOME_SHOWCASE_TOUR_QUERY_PARAM) ===
    HOME_SHOWCASE_TOUR_QUERY_VALUE
  );
}

export function isAutomationsTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(AUTOMATIONS_TOUR_QUERY_PARAM) ===
    AUTOMATIONS_TOUR_QUERY_VALUE
  );
}

export function isAutomationsAlertTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(AUTOMATIONS_ALERT_TOUR_QUERY_PARAM) ===
    AUTOMATIONS_ALERT_TOUR_QUERY_VALUE
  );
}

export function isBlogTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return searchParams.get(BLOG_TOUR_QUERY_PARAM) === BLOG_TOUR_QUERY_VALUE;
}

export function isBlogArticleTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(BLOG_ARTICLE_TOUR_QUERY_PARAM) ===
    BLOG_ARTICLE_TOUR_QUERY_VALUE
  );
}

export function isCommunityTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(COMMUNITY_TOUR_QUERY_PARAM) === COMMUNITY_TOUR_QUERY_VALUE
  );
}

export function isCommunityChatTourVisit(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(COMMUNITY_CHAT_TOUR_QUERY_PARAM) ===
    COMMUNITY_CHAT_TOUR_QUERY_VALUE
  );
}

/** Reads the home stage out of `?onboarding_home=` if present.
 *  - `1`            → "automations" (legacy boolean flag)
 *  - "automations"  → "automations"
 *  - "trade_ideas"  → "trade_ideas"
 *  - "community"    → "community"
 *  - "finale"       → "finale"
 *  - any other / missing value → null
 */
export function getHomeShowcaseStage(
  searchParams: URLSearchParams | null | undefined,
): HomeShowcaseStage | null {
  if (!searchParams) return null;
  const raw = searchParams.get(HOME_SHOWCASE_TOUR_QUERY_PARAM);
  if (!raw) return null;
  if (raw === HOME_SHOWCASE_TOUR_QUERY_VALUE) return "automations";
  if ((HOME_SHOWCASE_STAGE_VALUES as string[]).includes(raw)) {
    return raw as HomeShowcaseStage;
  }
  return null;
}

/** Persist the next home stage so a `?` -less navigation (e.g. the
 *  natural click on the sidebar Xynth logo from `/alerts/{id}`) still
 *  resumes the chain. The home page's `HomeShowcaseBuddyTour` consumes
 *  + clears this on mount and replaces the URL with the matching
 *  `?onboarding_home=<stage>` so refresh keeps working. */
export function setOnboardingResumeStage(stage: HomeShowcaseStage): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(HOME_TOUR_RESUME_STORAGE_KEY, stage);
  } catch {
    /* sessionStorage can throw in private mode / quota exhaustion —
       missing the resume just means the tour stops at the natural
       break point, which is acceptable. */
  }
}

export function consumeOnboardingResumeStage(): HomeShowcaseStage | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(HOME_TOUR_RESUME_STORAGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(HOME_TOUR_RESUME_STORAGE_KEY);
    if ((HOME_SHOWCASE_STAGE_VALUES as string[]).includes(raw)) {
      return raw as HomeShowcaseStage;
    }
    return null;
  } catch {
    return null;
  }
}
